import React, { useCallback, useEffect, useState } from "react";
import { Link, useLocation, useParams, useSearchParams } from "react-router-dom";
import { addPlusOnes, getManageRsvp } from "../api/rsvp";
import { createCheckout } from "../api/payments";
import { ManageRsvpResponse, PlusOneEntry } from "../types";
import { formatDate, formatPrice } from "../utils/format";
import Loading from "../components/Loading";
import ErrorMessage from "../components/ErrorMessage";
import PlusOneForm from "../components/PlusOneForm";

const coupleLabel = (wedding: ManageRsvpResponse["wedding"]) => {
  const names = [wedding.couple_name_1, wedding.couple_name_2].filter(Boolean);
  if (names.length) return names.join(" & ");
  return wedding.title;
};

const venueLabel = (wedding: ManageRsvpResponse["wedding"]) =>
  [wedding.venue_name, wedding.city, wedding.state].filter(Boolean).join(", ") ||
  wedding.location ||
  "";

const ManageRsvp: React.FC = () => {
  const { id, rsvpId } = useParams<{ id: string; rsvpId: string }>();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const seat = searchParams.get("seat");
  const [data, setData] = useState<ManageRsvpResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [actionError, setActionError] = useState("");
  const [notice, setNotice] = useState("");
  const [working, setWorking] = useState(false);
  const [payingId, setPayingId] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!rsvpId) return;
    setLoading(true);
    setError("");
    try {
      const { data: payload } = await getManageRsvp(rsvpId, seat);
      if (id && payload.wedding_id !== id) {
        setError("This RSVP does not match the wedding link.");
        setData(null);
        return;
      }
      setData(payload);
    } catch (err) {
      setError(err instanceof Error ? err.message : "RSVP not found");
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [id, rsvpId, seat]);

  useEffect(() => {
    load();
  }, [load]);

  useEffect(() => {
    const fromNav = (location.state as { notice?: string } | null)?.notice;
    if (fromNav) setNotice(fromNav);
  }, [location.state]);

  const startCheckout = async (payload: {
    plus_ones?: PlusOneEntry[];
    plus_one_guest_ids?: string[];
  }) => {
    if (!data) return;
    const { data: checkout } = await createCheckout({
      wedding_id: data.wedding_id,
      rsvp_id: data.rsvp_id,
      payment_type: "plus_ones",
      ...payload,
    });
    window.location.href = checkout.url;
  };

  const handlePayNew = async (plusOnes: PlusOneEntry[]) => {
    if (!data) return;
    setWorking(true);
    setActionError("");
    setNotice("");
    try {
      if (data.plus_one_price_cents > 0) {
        await startCheckout({ plus_ones: plusOnes });
        return;
      }
      const { data: result } = await addPlusOnes(data.rsvp_id, plusOnes);
      setNotice(
        result.email_warning || "Plus-ones added. They now appear on the couple's invite list."
      );
      await load();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : "Failed to add plus-ones");
    } finally {
      setWorking(false);
    }
  };

  const handleSelfPay = async (plusOnes: PlusOneEntry[]) => {
    if (!data) return;
    setWorking(true);
    setActionError("");
    setNotice("");
    try {
      const { data: result } = await addPlusOnes(data.rsvp_id, plusOnes);
      setNotice(
        result.email_warning ||
          "Plus-ones were added to the invite list.\nWe emailed them a payment link."
      );
      await load();
    } catch (err) {
      setActionError(err instanceof Error ? err.message : "Failed to add plus-ones");
    } finally {
      setWorking(false);
    }
  };

  const handlePayExisting = async (guestIds: string[]) => {
    if (!data || guestIds.length === 0) return;
    setPayingId(guestIds.length === 1 ? guestIds[0] : "all");
    setActionError("");
    setNotice("");
    try {
      await startCheckout({ plus_one_guest_ids: guestIds });
    } catch (err) {
      setActionError(err instanceof Error ? err.message : "Payment could not be started");
      setPayingId(null);
    }
  };

  if (loading) return <Loading message="Loading your RSVP..." />;
  if (error || !data) {
    return (
      <div className="page">
        <ErrorMessage message={error || "RSVP not found"} onRetry={load} />
      </div>
    );
  }

  const wedding = data.wedding;
  const venue = venueLabel(wedding);
  const unpaid = data.plus_ones.filter((p) => p.payment_status === "unpaid");
  const remaining = data.remaining_allotment;
  const allotmentExhausted = data.eligible && remaining <= 0;
  const plusOneViewer = data.viewer === "plus_one";
  const canManage = data.status === "confirmed" && (data.eligible || plusOneViewer);

  return (
    <div className="page">
      <h1>{plusOneViewer ? "Pay for your seat" : "Manage your RSVP"}</h1>
      <p className="form-hint">
        {plusOneViewer
          ? "No account needed. Complete payment below to confirm your seat."
          : "No account needed. Use this page anytime to add plus-ones or finish unpaid seats."}
      </p>

      <div className="form" style={{ marginBottom: "1.5rem" }}>
        <h3>{coupleLabel(wedding)}</h3>
        {wedding.date && <p className="wedding-date">{formatDate(wedding.date)}</p>}
        {venue && <p className="wedding-location">{venue}</p>}
        <p className="form-hint">
          Primary guest: <strong>{data.primary_guest.full_name}</strong>
          {data.status === "confirmed" ? " · RSVP confirmed" : ` · RSVP ${data.status}`}
        </p>
        {data.eligible && !plusOneViewer && (
          <p className="form-hint">
            Plus-one allotment: {data.plus_ones.length} of {data.plus_one_limit} used
            {remaining > 0 ? ` · ${remaining} remaining` : ""}.
            {data.plus_one_price_cents > 0
              ? ` Each additional seat is ${formatPrice(data.plus_one_price_cents)}.`
              : " Additional seats are complimentary."}
          </p>
        )}
      </div>

      {actionError && <div className="error-message inline">{actionError}</div>}
      {notice && (
        <div className="success-message" style={{ marginBottom: "1.5rem", whiteSpace: "pre-line" }}>
          {notice}
        </div>
      )}

      <div className="form" style={{ marginBottom: "1.5rem" }}>
        <h3>{plusOneViewer ? "Your seat" : "Plus-ones"}</h3>
        {data.plus_ones.length === 0 ? (
          <p className="form-hint">No plus-ones have been added yet.</p>
        ) : (
          <div className="guest-table-wrap">
            <table className="guest-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Payment</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {data.plus_ones.map((plusOne) => (
                  <tr key={plusOne.id}>
                    <td>{plusOne.full_name}</td>
                    <td>{plusOne.email || "—"}</td>
                    <td>
                      <span
                        className={`status-badge status-${
                          plusOne.payment_status === "paid" ? "confirmed" : "pending"
                        }`}
                      >
                        {plusOne.payment_status === "paid" ? "Paid" : "Unpaid"}
                      </span>
                    </td>
                    <td>
                      {canManage &&
                        plusOne.payment_status === "unpaid" &&
                        data.plus_one_price_cents > 0 && (
                          <button
                            type="button"
                            className="btn btn-primary btn-sm"
                            disabled={working || payingId !== null}
                            onClick={() => handlePayExisting([plusOne.id])}
                          >
                            {payingId === plusOne.id ? "Redirecting..." : "Pay this seat"}
                          </button>
                        )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {canManage && unpaid.length > 1 && data.plus_one_price_cents > 0 && !plusOneViewer && (
          <div className="button-row" style={{ marginTop: "1rem" }}>
            <button
              type="button"
              className="btn btn-secondary"
              disabled={working || payingId !== null}
              onClick={() => handlePayExisting(unpaid.map((p) => p.id))}
            >
              {payingId === "all" ? "Redirecting..." : `Pay all unpaid (${unpaid.length})`}
            </button>
          </div>
        )}
      </div>

      {!plusOneViewer && data.status !== "confirmed" && (
        <p className="form-hint">
          Confirm your RSVP on the{" "}
          <Link to={`/weddings/${data.wedding_id}`}>wedding page</Link> before adding plus-ones.
        </p>
      )}

      {canManage && allotmentExhausted && !plusOneViewer && (
        <div className="error-message inline">
          You've used all of your plus-one seats. No more plus-ones can be added.
        </div>
      )}

      {canManage && remaining > 0 && !plusOneViewer && (
        <PlusOneForm
          key={remaining}
          limit={remaining}
          ticketPrice={data.plus_one_price_cents}
          loading={working}
          onSubmit={handlePayNew}
          allowSelfPay={data.plus_one_price_cents > 0}
          onSelfPay={handleSelfPay}
          showSkip={false}
        />
      )}

      <div className="button-row" style={{ marginTop: "1.5rem" }}>
        <Link to={`/weddings/${data.wedding_id}`} className="btn btn-secondary">
          View wedding
        </Link>
      </div>
    </div>
  );
};

export default ManageRsvp;
